import { useState } from 'react';
import { Button, Card } from '@chessmate/ui';
import { useT } from '../i18n/useT.js';
import { PageHeader, PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';

export type LobbySide = 'white' | 'black' | 'random';

export interface LobbyInput {
  readonly player1Name: string;
  readonly player2Name: string;
  readonly firstSide: LobbySide;
}

export interface LobbyProps {
  readonly onPlay: (input: LobbyInput) => void;
  readonly onExit: () => void;
}

export function Lobby({ onPlay, onExit }: LobbyProps) {
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [side, setSide] = useState<LobbySide>('random');

  const { t } = useT('navigation');
  const { t: tCommon } = useT('common');

  const ready = p1.trim().length > 0 && p2.trim().length > 0;

  return (
    <ScreenLayout width="wide" data-testid="lobby" data-appdom-scope="lobby">
      <div className={`${PANEL_CLASS} mx-auto grid w-full max-w-6xl overflow-hidden lg:grid-cols-5`}>
        <div className="flex flex-col gap-4 p-6 sm:p-8 lg:col-span-2 lg:justify-center lg:p-10">
          <img src="/art/spot-friends.webp" alt={tCommon('art-alt-friends')} className="h-32 w-full rounded-2xl object-cover sm:h-44 lg:h-56" />
          <h1 className="font-display text-h1 font-bold text-fg lg:text-5xl" data-testid="lobby-title">
            {t('pass-and-play')}
          </h1>
          <p className="text-base font-semibold text-fg-muted lg:text-lg">
            Two players, one device.
          </p>
          <Button
            variant="secondary"
            className="self-start"
            data-testid="lobby-exit"
            data-appdom-id="lobby-exit"
            data-appdom-type="Button"
            onClick={onExit}
          >
            {tCommon('back')}
          </Button>
        </div>
      <Card
        className="glass glass-light rounded-none border-0 border-t border-border/60 p-6 shadow-none sm:p-8 lg:col-span-3 lg:border-l lg:border-t-0 lg:p-10"
        data-testid="lobby-card"
        data-appdom-id="lobby-card"
        data-appdom-type="Card"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-base font-bold text-ink">
              Player 1
              <input
                data-testid="lobby-player1-name"
                data-appdom-id="lobby-player1-name"
                data-appdom-type="TextInput"
                aria-label="Player 1 name"
                value={p1}
                onChange={(e) => setP1(e.target.value)}
                placeholder="Player 1"
                className="min-h-[52px] rounded-2xl border-2 border-sun/40 bg-white px-4 py-2 text-lg font-semibold text-ink dark:text-cream"
              />
            </label>
            <label className="flex flex-col gap-1 text-base font-bold text-ink">
              Player 2
              <input
                data-testid="lobby-player2-name"
                data-appdom-id="lobby-player2-name"
                data-appdom-type="TextInput"
                aria-label="Player 2 name"
                value={p2}
                onChange={(e) => setP2(e.target.value)}
                placeholder="Player 2"
                className="min-h-[52px] rounded-2xl border-2 border-sun/40 bg-white px-4 py-2 text-lg font-semibold text-ink dark:text-cream"
              />
            </label>
          </div>

          <fieldset
            className="flex flex-col gap-2"
            data-testid="lobby-side"
            data-appdom-id="lobby-side"
            data-appdom-type="RadioGroup"
          >
            <legend className="text-base font-bold text-ink">Who plays first?</legend>
            <div className="flex gap-2" role="radiogroup">
              {(['white', 'black', 'random'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  // biome-ignore lint/a11y/useSemanticElements: visual design requires button styling + keyboard activation; radio role is correct inside the role="radiogroup" parent inside Lobby's fieldset
                  role="radio"
                  aria-checked={side === s}
                  data-testid={`lobby-side-${s}`}
                  data-appdom-id={`lobby-side-${s}`}
                  data-appdom-type="Radio"
                  onClick={() => setSide(s)}
                  className={`min-h-[52px] flex-1 rounded-2xl border-2 px-3 py-2 text-base font-bold transition-colors ${
                    side === s
                      ? 'border-berry bg-berry text-white'
                      : 'border-sun/40 bg-cream text-ink hover:bg-sun/20 dark:border-sun/20'
                  }`}
                >
                  {s === 'random' ? 'Random' : s === 'white' ? 'White' : 'Black'}
                </button>
              ))}
            </div>
          </fieldset>

          <Button
            data-testid="lobby-start"
            data-appdom-id="lobby-start"
            data-appdom-type="Button"
            disabled={!ready}
            onClick={() => onPlay({ player1Name: p1.trim(), player2Name: p2.trim(), firstSide: side })}
          >
            {tCommon('start-game')}
          </Button>

          <p className="text-sm font-semibold text-fg-muted">
            Pass-and-play game engine ships in a follow-up release.
          </p>
        </div>
      </Card>
      </div>
    </ScreenLayout>
  );
}
